import { useState } from 'preact/hooks'
import { Building2, Globe, Users, Edit, ExternalLink, UserCheck, Plus, ScanSearch } from 'lucide-preact'
import type { ClientCompanyViewDto, WorkspaceCreateDto, WorkspaceUpdateDto } from '@/types/company'
import { clientCompanyApi } from '@/api/clientCompany'
import { useToast } from '@/hooks/useToast'
import { useCompanyTracking } from '@/hooks/useCompanyTracking'
import { THEME_COLORS } from '../../../constants/theme'
import { CompanyViewDetails } from './CompanyViewDetails'
import { WorkspaceEditForm } from './WorkspaceEditForm'
import { WorkspaceCard, type WorkspaceSaveOutcome } from './WorkspaceCard'
import { VisitorTrackingPanel } from '@/components/tracking/VisitorTrackingPanel'
import { ApprovedDomainsCard } from '@/components/tracking/ApprovedDomainsCard'

// Helper Components
const StatusBadge = ({ status, variant = 'default' }: { status: string; variant?: 'default' | 'light' }) => {
  const getStatusStyles = (status: string, variant: string) => {
    const statusKey = status.toLowerCase()

    if (statusKey === 'active') {
      return variant === 'light'
        ? {
          backgroundColor: THEME_COLORS.BACKGROUNDS.LIGHT_SUBTLE,
          color: THEME_COLORS.GREEN[600],
          borderColor: THEME_COLORS.BORDERS.LIGHT,
        }
        : {
          backgroundColor: 'rgba(34, 197, 94, 0.2)',
          color: THEME_COLORS.GREEN[200],
          borderColor: 'rgba(34, 197, 94, 0.3)',
        }
    }

    if (statusKey === 'inactive') {
      return variant === 'light'
        ? {
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          color: '#dc2626',
          borderColor: 'rgba(239, 68, 68, 0.3)',
        }
        : {
          backgroundColor: 'rgba(239, 68, 68, 0.2)',
          color: '#fca5a5',
          borderColor: 'rgba(248, 113, 113, 0.3)',
        }
    }

    return variant === 'light'
      ? {
        backgroundColor: 'rgba(100, 116, 139, 0.1)',
        color: '#374151',
        borderColor: 'rgba(100, 116, 139, 0.3)',
      }
      : {
        backgroundColor: 'rgba(100, 116, 139, 0.2)',
        color: '#cbd5e1',
        borderColor: 'rgba(148, 163, 184, 0.3)',
      }
  }

  const styles = getStatusStyles(status, variant)

  return (
    <span className="px-2 py-1 rounded-md text-xs font-medium border backdrop-blur-sm" style={styles}>
      {status}
    </span>
  )
}

const LinkedInButton = ({ url }: { url: string }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-2 transition-colors duration-200"
    style={{ color: THEME_COLORS.GREEN[600] }}
    onMouseEnter={(e) => {
      e.currentTarget.style.color = THEME_COLORS.GREEN[500]
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.color = THEME_COLORS.GREEN[600]
    }}
  >
    <UserCheck className="h-4 w-4" />
    LinkedIn
  </a>
)

interface CompanyHeaderProps {
  company: ClientCompanyViewDto
  onEditCompany: () => void
  newWorkspace: WorkspaceCreateDto | null
  setNewWorkspace: (workspace: WorkspaceCreateDto | null) => void
  loading: boolean
  handleAddWorkspace: () => void
  handleSaveWorkspace: () => Promise<WorkspaceSaveOutcome>
  handleDeleteWorkspace: (workspaceId: string) => void
  handleUpdateWorkspace: (workspace: WorkspaceUpdateDto) => Promise<WorkspaceSaveOutcome>
}

export function CompanyHeader({
  company,
  onEditCompany,
  newWorkspace,
  setNewWorkspace,
  loading,
  handleAddWorkspace,
  handleSaveWorkspace,
  handleDeleteWorkspace,
  handleUpdateWorkspace,
}: CompanyHeaderProps) {
  const { showSuccess, showError } = useToast()
  const [scraping, setScraping] = useState(false)
  const {
    tracking,
    isLoading: trackingLoading,
    error: trackingError,
    setEnabled,
    saveScripts,
    saveApprovedDomains,
  } = useCompanyTracking(company.Id)
  const companyWebsite = { companyName: company.Name, companyWebsite: company.CompanyUrl }

  const handleScrapeWebsite = async () => {
    setScraping(true)
    const res = await clientCompanyApi.scrapeWebsite(company.Id)
    setScraping(false)
    if (res.error) {
      showError('Scrape failed', res.error)
    } else {
      showSuccess('Scrape complete', 'Website has been scraped and brand data has been updated.')
    }
  }

  return (
    <div className="space-y-6">
      {/* Company Header Card */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div
          className="px-6 py-8"
          style={{ background: `linear-gradient(to right, ${THEME_COLORS.GREEN[600]}, ${THEME_COLORS.GREEN[500]})` }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                <Building2 className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">{company.Name}</h1>
                <div className="flex items-center gap-4 text-white/90">
                  <span className="flex items-center gap-1">
                    <Globe className="h-4 w-4" />
                    {company.Industry}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {company.Size}
                  </span>
                  <StatusBadge status={company.Status} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-700/50 border-t border-slate-200 dark:border-slate-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm">
              {company.CompanyUrl && (
                <a
                  href={company.CompanyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 transition-colors duration-200"
                  style={{ color: THEME_COLORS.GREEN[600] }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = THEME_COLORS.GREEN[500]
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = THEME_COLORS.GREEN[600]
                  }}
                >
                  <ExternalLink className="h-4 w-4" />
                  Website
                </a>
              )}
              {company.LinkedInUrl && <LinkedInButton url={company.LinkedInUrl} />}
              {company.CompanyUrl && (
                <button
                  onClick={handleScrapeWebsite}
                  disabled={scraping}
                  className="flex items-center gap-2 text-sm transition-colors duration-200 disabled:opacity-50"
                  style={{ color: THEME_COLORS.GREEN[600] }}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled) e.currentTarget.style.color = THEME_COLORS.GREEN[500]
                  }}
                  onMouseLeave={(e) => {
                    if (!e.currentTarget.disabled) e.currentTarget.style.color = THEME_COLORS.GREEN[600]
                  }}
                  title="Scrape company website for brand data (colors, fonts, content)"
                >
                  {scraping ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <ScanSearch className="h-4 w-4" />
                  )}
                  {scraping ? 'Scraping...' : 'Scrape Website'}
                </button>
              )}
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              {(company.Workspaces || []).length} workspace{(company.Workspaces || []).length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Company and Workspace Management */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Company Details + Approved Domains */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Company Information</h3>
              <button
                onClick={onEditCompany}
                className="p-2 text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                title="Edit Company"
              >
                <Edit className="w-4 h-4" />
              </button>
            </div>
            <CompanyViewDetails company={company} />
          </div>

          <ApprovedDomainsCard
            tracking={tracking}
            isLoading={trackingLoading}
            error={trackingError}
            saveApprovedDomains={saveApprovedDomains}
          />

        </div>

        {/* Workspaces + Visitor Tracking */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Company Workspaces</h3>
              <button
                onClick={handleAddWorkspace}
                disabled={!!newWorkspace}
                className="text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: THEME_COLORS.GREEN[600] }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = THEME_COLORS.GREEN[500]
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = THEME_COLORS.GREEN[600]
                  }
                }}
              >
                <Plus className="h-4 w-4" />
                Add Workspace
              </button>
            </div>

            {newWorkspace && (
              <div className="mb-6 p-4 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800">
                <WorkspaceEditForm
                  workspace={newWorkspace}
                  company={companyWebsite}
                  onChange={setNewWorkspace}
                  onSave={handleSaveWorkspace}
                  onCancel={() => setNewWorkspace(null)}
                  loading={loading}
                  isNew={true}
                />
              </div>
            )}

            <div className="space-y-4">
              {(company.Workspaces || []).map((workspace) => (
                <WorkspaceCard
                  key={workspace.Id}
                  workspace={workspace}
                  company={companyWebsite}
                  onDelete={handleDeleteWorkspace}
                  onUpdate={handleUpdateWorkspace}
                  loading={loading}
                />
              ))}

              {company.Workspaces.length === 0 && (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 text-slate-400 dark:text-slate-500 mx-auto mb-4" />
                  <div className="text-slate-600 dark:text-slate-300 mb-2">No workspaces found</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Create your first workspace to get started</div>
                </div>
              )}
            </div>
          </div>

          <VisitorTrackingPanel
            tracking={tracking}
            isLoading={trackingLoading}
            error={trackingError}
            setEnabled={setEnabled}
            saveScripts={saveScripts}
          />
        </div>
      </div>
    </div>
  )
}
